import type { KeyArgsFunction } from "@apollo/client/cache/inmemory/policies";
import type { relayStylePagination } from "@apollo/client/utilities";

type RelayPolicy = ReturnType<typeof relayStylePagination>;
type RelayMerge = Exclude<NonNullable<RelayPolicy["merge"]>, boolean>;
type RelayRead = NonNullable<RelayPolicy["read"]>;

// ConnectedTable asks for a numbered page with `after: "offset:N"`.
export const isNumberedPage = (args: Record<string, unknown> | null) =>
  typeof args?.after === "string" && args.after.startsWith("offset:");

// relayStylePagination keeps one list per connection and appends whatever is
// fetched after a cursor. A numbered page is a page of its own: it gets its
// own cache entry (its offset and size are part of the key) and a refetch
// replaces it. Appended, page 1 would show every page fetched so far.
export function withNumberedPages(policy: RelayPolicy): RelayPolicy {
  const relayMerge = policy.merge as RelayMerge;
  const { read: relayRead } = policy as { read: RelayRead };
  const relayKeyArgs = policy.keyArgs as KeyArgsFunction;

  const keyArgs: KeyArgsFunction = (args, context) => {
    const key = relayKeyArgs(args, context);
    return isNumberedPage(args) && Array.isArray(key)
      ? [...(key as string[]), "after", "limit"]
      : key;
  };

  const merge: RelayMerge = (existing, incoming, options) =>
    isNumberedPage(options.args)
      ? (incoming as ReturnType<RelayMerge>)
      : relayMerge(existing, incoming, options);

  const read: RelayRead = (existing, options) =>
    isNumberedPage(options.args)
      ? (existing as ReturnType<RelayRead>)
      : relayRead(existing, options);

  return { ...policy, keyArgs, merge, read };
}
